import { type SetStateAction, useCallback, useMemo, useRef } from 'react';
import { useDrop } from 'react-dnd';

import styled from '@emotion/styled';
import { DropdownMenu2, MenuOptionItem } from '@kc-console/common-ui';
import { type FavoriteService } from '@kc-console/context';

import { type DropSide, FAVORITE_DRAG_TYPE, type FavoriteDragItem } from './constants';
import FavoriteMoreItem from './FavoriteMoreItem';
import { getInsertLine, toLocalDropGap } from './utils';

import type { DropdownMenu2ItemData } from '@kc-console/common-ui';

interface FavoriteMoreListProps {
  allItems: FavoriteService[];
  anchorEl: HTMLButtonElement | null;
  dropIndicatorGap: number | null;
  favoriteMoreItems: FavoriteService[];
  isMoreMenuOpen: boolean;
  onDragEnd: (didDrop: boolean) => void;
  onDragHover: (from: number, to: number, side: DropSide) => void;
  onDrop: () => void;
  setIsMoreMenuOpen: (value: SetStateAction<boolean>) => void;
}

const FavoriteMoreList = ({
  allItems,
  anchorEl,
  dropIndicatorGap,
  favoriteMoreItems,
  isMoreMenuOpen,
  onDragEnd,
  onDrop,
  onDragHover,
  setIsMoreMenuOpen,
}: FavoriteMoreListProps) => {
  const contextMenuOpenRef = useRef(false);

  const dropdownItems: DropdownMenu2ItemData[] = useMemo(() => {
    // dropIndicatorGap(글로벌 인덱스)을 favoriteMoreItems 내 로컬 gap 위치(0..length)로 변환
    const localDropGap = toLocalDropGap(dropIndicatorGap, favoriteMoreItems, allItems);

    // 드롭 indicator를 별도 list item으로 splice하면 인덱스가 밀려 DropdownMenu2의
    // index 기반 key가 바뀌고 드래그 중 FavoriteMoreItem이 remount되어 drop 타깃이 끊긴다.
    // 따라서 항목 배열 길이는 고정하고, 각 항목에 insertLine 위치만 넘겨 absolute 라인으로 표시한다.
    return favoriteMoreItems.map((service, localPos) => {
      // allItems(localOrder) 기준 전체 인덱스를 계산하여 DnD 순서 변경에 활용
      const index = allItems.findIndex(s => s.name === service.name);
      const insertLine = getInsertLine(localDropGap, localPos, favoriteMoreItems.length);
      return {
        type: 'option',
        text: null,
        interactive: false,
        renderItem: params => {
          return (
            <StyledDropdownMenuOptionItem
              {...params}
              key={params.key}
              interactive={true}
              renderContent={() => {
                return (
                  <FavoriteMoreItem
                    service={service}
                    index={index}
                    insertLine={insertLine}
                    onDragHover={onDragHover}
                    onDragEnd={onDragEnd}
                    onServiceClick={() => setIsMoreMenuOpen(false)}
                    onContextMenuOpen={() => {
                      contextMenuOpenRef.current = true;
                    }}
                    onContextMenuClose={() => {
                      contextMenuOpenRef.current = false;
                    }}
                  />
                );
              }}
            />
          );
        },
      };
    });
  }, [allItems, dropIndicatorGap, favoriteMoreItems, onDragEnd, onDragHover, setIsMoreMenuOpen]);

  // 메뉴 전체를 단일 drop 타깃으로 위임한다. 아이템 사이 gap/indicator/padding 어디에
  // 떨어뜨려도 drop을 받으며, 삽입 위치는 아이템 hover로 갱신된 dragState가 결정한다.
  // 메뉴(Popper) 위 드롭에서 monitor.didDrop()이 false로 보고되는 경우를 대비해 onDrop으로도 신호를 보낸다.
  const [, drop] = useDrop<FavoriteDragItem>({
    accept: FAVORITE_DRAG_TYPE,
    drop: () => {
      onDrop();
      return { dropped: true };
    },
  });

  const setMenuRef = useCallback(
    (node: HTMLDivElement | null) => {
      drop(node);
    },
    [drop],
  );

  return (
    <DropdownMenu2
      ref={setMenuRef}
      anchorEl={anchorEl}
      open={isMoreMenuOpen}
      origin="bottom right"
      marginThreshold={0}
      onClose={() => {
        if (contextMenuOpenRef.current) return;
        setIsMoreMenuOpen(false);
      }}
      items={dropdownItems}
      color="global"
    />
  );
};

export default FavoriteMoreList;

const StyledDropdownMenuOptionItem = styled(MenuOptionItem)`
  padding: 0;
`;
